import { useState } from "react";
import { KeyRound, Copy, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PictureGrid } from "@/components/kids/PictureGrid";

const input = "w-full px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]";
const LENGTHS = [["short", "Short"], ["standard", "Standard"], ["deep", "Deep"]];

export const KidLoginSetup = ({ kid, onChange, idSuffix = "" }) => {
  const [open, setOpen] = useState(false);
  const [u, setU] = useState(kid.username || kid.name.split(" ")[0].toLowerCase());
  const [pics, setPics] = useState([]);
  const [code, setCode] = useState(null);
  const show = async () => { setOpen(true); setPics([]); setCode((await http.get("/family/kid-login")).data.family_code); };
  const save = async (e) => {
    e.preventDefault();
    if (pics.length !== 3) return toast.error("Pick 3 pictures");
    try { const r = (await http.put(`/family/children/${kid.id}/kid-login`, { username: u, pictures: pics })).data; toast.success(`Picture login ready for ${kid.name.split(" ")[0]}`); setCode(r.family_code); setOpen(false); onChange(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <>
      <button onClick={show} data-testid={`kid-login-setup-${kid.id}${idSuffix}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E6DEC3] bg-white text-xs text-[#2C4A3E]">
        <KeyRound className="w-3.5 h-3.5" />{kid.picture_login ? `Picture login: ${kid.username}` : "Set up picture login"}
      </button>
      {kid.picture_login && <Link to={`/parent/login-cards?kid=${kid.id}`} data-testid={`kid-login-print-${kid.id}${idSuffix}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E6DEC3] bg-white text-xs text-[#2C4A3E]"><Printer className="w-3.5 h-3.5" />Print login card</Link>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FDFBF7]" data-testid={`kid-login-dialog-${kid.id}${idSuffix}`}>
          <DialogTitle className="font-display text-2xl text-[#2C4A3E]">Picture login for {kid.name.split(" ")[0]}</DialogTitle>
          {code && <p className="text-sm text-stone-600">Family code: <b className="tracking-widest text-[#2C4A3E]" data-testid="family-code">{code}</b>
            <button onClick={() => { navigator.clipboard?.writeText(code); toast.success("Copied"); }} className="ml-2 inline-flex align-middle" aria-label="Copy family code"><Copy className="w-4 h-4" /></button>
            <span className="block text-xs mt-1">Kids go to <b>consciouslearn.com/kids</b>, type this code, tap their name, then their 3 pictures.</span></p>}
          <form onSubmit={save} className="space-y-4">
            <label className="block text-sm text-[#2C4A3E]">Username<input value={u} onChange={(e) => setU(e.target.value.toLowerCase())} minLength={3} maxLength={20} required data-testid="kid-username-input" className={`${input} mt-1`} /></label>
            <div><p className="text-sm text-[#2C4A3E] mb-2">Choose 3 pictures in order {kid.picture_login && "(this replaces the old ones)"}</p><PictureGrid value={pics} onChange={setPics} testId="setup-pic" /></div>
            <button data-testid="kid-login-save" className="w-full py-3 rounded-full bg-[#C87D53] text-white">Save picture login</button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const LessonLength = ({ kid, onChange }) => {
  const set = async (v) => {
    try { await http.put(`/family/children/${kid.id}/lesson-length`, { lesson_length: v }); toast.success(`${kid.name.split(" ")[0]}'s lessons: ${v}`); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-[#F1EADB] p-1 text-xs" data-testid={`lesson-length-${kid.id}`}>
      <span className="px-2 text-stone-600">Lessons:</span>
      {LENGTHS.map(([v, l]) => <button key={v} onClick={() => set(v)} data-testid={`lesson-length-${kid.id}-${v}`} className={`px-3 py-1 rounded-full ${kid.lesson_length === v ? "bg-[#2C4A3E] text-white" : "text-[#2C4A3E]"}`}>{l}</button>)}
    </div>
  );
};
